import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { selectAmendHistoryId, selectedRequestAmendment, selectProposalAmendId } from "../../features/actuarial/request-amendment/request-amendment.slice";
import { useGetRequestAmendmentsLogsQuery } from "../../api/request-amendment/request-amendment-api";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { usePopulateProposed } from "./use-populate-proposed";
import { ProposalData } from "../../api/gms/proposal/proposal-api-state";

export const useProposeData = () => {
    const proposalAmendId = useSelector(selectProposalAmendId);
    const requestAmendmentData = useSelector(selectedRequestAmendment);
    const amendHistoryId = useSelector(selectAmendHistoryId);

    const {
        data: getAmendmentHistoryLogs,
        isLoading: isLoadingGetAmendmentHistoryLogs,
        isError: isErrorGetAmendmentHistoryLogs,
        error: errorGetAmendmentHistoryLogs,
        refetch: refetchGetAmendmentHistoryLogs,
        isFetching: isFetchingGetAmendmentHistoryLogs
    } = useGetRequestAmendmentsLogsQuery(
        proposalAmendId !== null
            ? { proposalId: proposalAmendId }
            : skipToken,
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const errorMessageGetAmendmentHistoryLogs = getErrorMessage(
        errorGetAmendmentHistoryLogs
    );

    const amendId = requestAmendmentData?.amendment_id || amendHistoryId;

    const selectedAmendment = useMemo(
        () => getAmendmentHistoryLogs?.find(
            item => item.amendment_id === amendId
        ),
        [getAmendmentHistoryLogs, amendId]
    );

    const proposal = useMemo(
        () => selectedAmendment?.proposed_data ?? undefined,
        [selectedAmendment]
    );

    const [proposedAmendment, setProposedAmendment] = useState<ProposalData | null>(null);
    
    
    const {
        populateChannelType,
        populateAffiliates,
        populateCommissionRateAndServiceFee,
        populateBasicInfo,
        populateClassification,
        populateBusinessType,
        populateTypeOfGroup,
        populateAgeProfile,
        populateFund,
        populateProposalCategory,
        populateAmountOfLoans,
        populateCoverage,
        populateGYRTorGPA,
        populateRidersPlan,
        populatePayments,
        populateBorrowAge,
        populateNotes,
        populateRiders,
        populateTypeOfProduct,
        populateCSVFile,
        populateBorrowerAge1865ForComputation,
        populateProposalRemainingAge,
        populateProposalPreparedBy,
        populateProposalStatus,
        populateProposalSupportingDocuments,
        // populateApproval,
    } = usePopulateProposed(proposal);

    const repopulateProposeAll = useCallback(() => {
        populateChannelType();
        populateAffiliates();
        populateCommissionRateAndServiceFee();
        populateBasicInfo();
        populateClassification();
        populateBusinessType();
        populateTypeOfGroup();
        populateAgeProfile();
        populateFund();
        populateProposalCategory();
        populateAmountOfLoans();
        populateCoverage();
        populateGYRTorGPA();
        populateRidersPlan();
        populatePayments();
        populateBorrowAge();
        populateNotes();
        populateRiders();
        populateTypeOfProduct();
        populateCSVFile();
        populateBorrowerAge1865ForComputation();
        populateProposalRemainingAge();
        populateProposalPreparedBy();
        populateProposalStatus();
        populateProposalSupportingDocuments();
        // populateApproval();
    }, [
        populateChannelType,
        populateAffiliates,
        populateCommissionRateAndServiceFee,
        populateBasicInfo,
        populateClassification,
        populateBusinessType,
        populateTypeOfGroup,
        populateAgeProfile,
        populateFund,
        populateProposalCategory,
        populateAmountOfLoans,
        populateCoverage,
        populateGYRTorGPA,
        populateRidersPlan,
        populatePayments,
        populateBorrowAge,
        populateNotes,
        populateRiders,
        populateTypeOfProduct,
        populateCSVFile,
        populateBorrowerAge1865ForComputation,
        populateProposalRemainingAge,
        populateProposalPreparedBy,
        populateProposalStatus,
        populateProposalSupportingDocuments,
        // populateApproval,
    ]);

    // useEffect(() => {
    //     if (!proposal?.application_id) {
    //         return;
    //     }

    //     repopulateProposeAll();
    // }, [
    //     proposal?.application_id,
    //     repopulateProposeAll,
    // ]);

    return {
        repopulateProposeAll,

        populateBorrowerAge1865ForComputation,
        populateProposalRemainingAge,

        isLoadingGetAmendmentHistoryLogs,
        isErrorGetAmendmentHistoryLogs,
        errorMessageGetAmendmentHistoryLogs,
        refetchGetAmendmentHistoryLogs,
        isFetchingGetAmendmentHistoryLogs,

        selectedAmendment,
        proposal,

        proposedAmendment,
        setProposedAmendment,
    };
};